import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";

const INK_MS = 1100;
const SLIDE_MS = 620;
const SLIDE_MS_REDUCED = 100;

/**
 * Post-login boot: horizontal blue-ink bar fills on `--t-bg`, then the sheet
 * collapses toward the top to reveal the destination page.
 */
export function PortalBootOverlay({ onFinished }) {
  /** `ink` — bar fills; `slide` — white sheet scales away */
  const [phase, setPhase] = useState("ink");
  const finishedRef = useRef(false);

  const slideMs = useMemo(() => {
    if (typeof window === "undefined") return SLIDE_MS;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches ? SLIDE_MS_REDUCED : SLIDE_MS;
  }, []);

  const finishOnce = useCallback(() => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    onFinished();
  }, [onFinished]);

  const handleSlideTransitionEnd = useCallback(
    (e) => {
      if (phase !== "slide") return;
      if (e.propertyName !== "transform") return;
      finishOnce();
    },
    [phase, finishOnce],
  );

  useEffect(() => {
    const reduced =
      typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const delay = reduced ? 120 : INK_MS + 180;
    const id = window.setTimeout(() => setPhase("slide"), delay);
    return () => clearTimeout(id);
  }, []);

  useEffect(() => {
    if (phase !== "slide") return undefined;
    const id = window.setTimeout(() => finishOnce(), slideMs + 120);
    return () => clearTimeout(id);
  }, [phase, finishOnce, slideMs]);

  const node = (
    <div
      className="fixed inset-0 z-[9999] overflow-hidden pointer-events-none"
      role="progressbar"
      aria-busy={phase === "ink"}
      aria-valuemin={0}
      aria-valuemax={100}
      data-testid="portal-boot-overlay"
    >
      <div
        className={`pointer-events-auto absolute inset-0 flex origin-top items-center justify-center bg-[var(--t-bg)] shadow-none transition-transform will-change-transform ${
          phase === "slide" ? "scale-y-0" : "scale-y-100"
        }`}
        style={{
          transitionDuration: `${slideMs}ms`,
          transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
          transitionProperty: "transform",
        }}
        onTransitionEnd={handleSlideTransitionEnd}
      >
        {phase === "ink" ? (
          <div className="portal-boot-ink" aria-hidden>
            <div className="portal-boot-ink__track">
              <div className="portal-boot-ink__fill" />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );

  return createPortal(node, document.body);
}
